/**
 * Onglet Positions -- Tableau des positions planetaires
 * Reproduit pixel-perfect le tableau de client-view.php
 */

import { colors } from '../../styles/tokens.js';
import { PLANET_SYMBOLS, ROMAN } from './planetColors.js';
import type { PlanetPositionData } from '../../types/clients.js';

/** Planetes affichees dans l'ordre du PHP */
const PLANETES_AFFICHEES = [
  'Soleil', 'Lune', 'Mercure', 'Venus', 'Mars',
  'Jupiter', 'Saturne', 'Uranus', 'Neptune', 'Pluton',
  'Ascendant', 'Milieu de Ciel', 'Noeud Lunaire Nord',
];

/** Alias pour matcher les noms du CSV */
const ALIASES: Record<string, string> = {
  'Venus': 'Venus',
  'Milieu du Ciel': 'Milieu de Ciel',
  'MC': 'Milieu de Ciel',
  'AS': 'Ascendant',
  'Noeud Nord': 'Noeud Lunaire Nord',
  'Noeud N.': 'Noeud Lunaire Nord',
  'Noeud N': 'Noeud Lunaire Nord',
};

interface Props {
  planetes: PlanetPositionData[];
  importDate?: string;
}

export function PositionsTab({ planetes, importDate }: Props) {
  // Filtrer et ordonner comme le PHP
  const filtered: PlanetPositionData[] = [];
  const seen = new Set<string>();

  for (const nomCible of PLANETES_AFFICHEES) {
    if (seen.has(nomCible)) continue;

    // Chercher la planete par nom direct ou alias
    const found = planetes.find((p) => {
      const normalized = ALIASES[p.nom] ?? p.nom;
      return normalized === nomCible || p.nom === nomCible;
    });

    if (found) {
      filtered.push(found);
      seen.add(nomCible);
    }
  }

  if (filtered.length === 0) {
    return <div style={{ fontSize: 14, color: colors.textMuted }}>Aucune position disponible.</div>;
  }

  return (
    <div style={{
      background: '#fff',
      borderRadius: 8,
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {/* Section header — style PHP */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.25rem',
        paddingBottom: '0.75rem',
        borderBottom: `2px solid ${colors.border}`,
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
          <i className="bi bi-globe" style={{ color: colors.accent, marginRight: 8 }} />
          Positions Planetaires
        </h3>
        {importDate && (
          <small style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
            Importe le {formatImportDate(importDate)}
          </small>
        )}
      </div>

      <div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: colors.bgPage }}>
              <th style={thStyle}>Planete</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Position</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Signe</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Maison</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const displayName = getDisplayName(p.nom);
              const symbole = PLANET_SYMBOLS[p.nom] ?? PLANET_SYMBOLS[displayName] ?? '';
              const isRetro = !!p.retrograde;

              return (
                <tr key={p.nom}>
                  <td style={tdStyle}>
                    <span style={{
                      fontFamily: "'Noto Sans Symbols 2', sans-serif",
                      marginRight: 8,
                      fontSize: '1.1rem',
                    }}>
                      {symbole}
                    </span>
                    {displayName}
                    {isRetro && (
                      <span style={{ color: '#9333ea', fontSize: '0.8rem', marginLeft: 4 }}>&#8478;</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontFamily: 'monospace' }}>
                    {p.degre}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 500 }}>
                    {p.signe}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {typeof p.maison === 'number' && p.maison >= 1 && p.maison <= 12
                      ? ROMAN[p.maison - 1]
                      : p.maison ?? ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Formate la date d'import en FR */
function formatImportDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/** Nom d'affichage propre */
function getDisplayName(nom: string): string {
  if (nom === 'Noeud Lunaire Nord' || nom === 'Noeud Nord' || nom === 'Noeud N.' || nom === 'Noeud N') return 'Noeud Lunaire Nord';
  if (nom === 'Milieu du Ciel' || nom === 'MC') return 'Milieu de Ciel';
  return nom;
}

const thStyle: React.CSSProperties = {
  padding: 10,
  textAlign: 'left',
  borderBottom: `2px solid ${colors.border}`,
  fontWeight: 600,
  fontSize: '0.85rem',
};

const tdStyle: React.CSSProperties = {
  padding: 10,
  borderBottom: `1px solid ${colors.border}`,
};
